'use client'

import { PlinkoRisk, PlinkoRows } from '@/api/v2/types'
import DiceSlider from '@/components/games/dice/DiceSlider'
import KenoTiles from '@/components/games/keno/tiles'
import MinesTiles from '@/components/games/mines/tiles'
import PlinkoSlot from '@/components/games/plinko-v2/PlinkoSlot'
import {
  PLINKO_PAYOUTS,
  PLINKO_PAYOUT_COLORS,
} from '@/components/games/plinko-v2/contants'
import RouletteGameBoard from '@/components/games/roulette/board'
import PlayingCard, {
  PlayingCardType,
} from '@/components/games/shared/playingCard'
import TowerGameTile from '@/components/games/tower/tile'
import { TowerGameDifficulty } from '@/components/games/tower/types'
import { WHEEL_PAYOUTS } from '@/components/games/wheel/constants'
import {
  WheelGameRisk,
  WheelGameSegments,
} from '@/components/games/wheel/types'
import WheelGameWheel from '@/components/games/wheel/wheel'
import HelpSearchIcon from '@/components/icons/help-search'
import { Text } from '@/components/ui/text'
import calculateDiceBetResult from '@/lib/provably-fair/calculation/dice'
import calculateHiloBetResult from '@/lib/provably-fair/calculation/hilo'
import calculateKenoBetResult from '@/lib/provably-fair/calculation/keno'
import calculateLimboBetResult from '@/lib/provably-fair/calculation/limbo'
import calculateMinesBetResult from '@/lib/provably-fair/calculation/mines'
import calculatePlinkoBetResult from '@/lib/provably-fair/calculation/plinko'
import calculateRouletteBetResult from '@/lib/provably-fair/calculation/roulette'
import calculateTowerBetResult from '@/lib/provably-fair/calculation/tower'
import calculateWheelBetResult from '@/lib/provably-fair/calculation/wheel'
import { Box, Flex, Stack } from '@/styled-system/jsx'
import { useSearchParams } from 'next/navigation'
import Spinner from '../design-system/spinner'

export type FairnessVerifyResultProps = {
  isLoading?: boolean
}

export default function FairnessVerifyResult({
  isLoading,
}: FairnessVerifyResultProps) {
  const searchParams = useSearchParams()

  const game = searchParams.get('game') ?? ''
  const clientSeed = searchParams.get('clientSeed') ?? ''
  const serverSeed = searchParams.get('serverSeed') ?? ''
  const nonce = Number(searchParams.get('nonce')) || 0

  if (!game || !clientSeed || !serverSeed) {
    return (
      <Flex align='center' gap={4}>
        <HelpSearchIcon color='primary' size='xl' />
        <Stack gap={0}>
          <Text color='white' fontSize='sm' fontWeight='600'>
            OINK...No results found!
          </Text>
          <Text color='#A997B5' fontSize='sm'>
            More inputs are required to verify result
          </Text>
        </Stack>
      </Flex>
    )
  }

  if (isLoading) {
    return <Spinner />
  }

  switch (game) {
    case 'dice':
      const diceRoll = calculateDiceBetResult({
        clientSeed,
        serverSeed,
        nonce,
      })

      return (
        <DiceSlider
          target={0}
          result={diceRoll}
          condition='over'
          showResult
          isDisabled
        />
      )

    case 'hilo':
      const cards = calculateHiloBetResult({
        clientSeed,
        serverSeed,
        nonce,
      })

      return (
        <Box overflowX='auto'>
          <Flex gap={2}>
            {cards.map((result, index) => {
              const suit = {
                '♣': 'C',
                '♦': 'D',
                '♥': 'H',
                '♠': 'S',
              }[result[0]]
              const rank = result.slice(1)

              return (
                <Box key={index} fontSize={{ base: '0.5em', md: '0.75em' }}>
                  <PlayingCard
                    card={
                      {
                        suit,
                        rank,
                      } as PlayingCardType
                    }
                  />
                </Box>
              )
            })}
          </Flex>
        </Box>
      )

    case 'keno':
      const numbers = new Set(
        calculateKenoBetResult({
          clientSeed,
          serverSeed,
          nonce,
        })
      )

      return (
        <KenoTiles
          selectedNumbers={numbers}
          drawnNumbers={numbers}
          isDisabled
        />
      )

    case 'limbo':
      const limboResult = calculateLimboBetResult({
        clientSeed,
        serverSeed,
        nonce,
      })

      return (
        <Text color='text.primary' fontSize='lg' fontWeight='bold'>
          {limboResult.toFixed(2)} ×
        </Text>
      )

    case 'mines':
      const minesCount = Number(searchParams.get('minesCount') ?? 3)

      const mines = calculateMinesBetResult({
        clientSeed,
        serverSeed,
        nonce,
        minesCount,
      })

      const tiles = Array.from(
        { length: 25 },
        (_, index) =>
          ({
            status: mines.includes(index) ? 'mine' : 'gem',
            revealed: true,
          }) as const
      )

      return <MinesTiles tiles={tiles} w='70%' />

    case 'plinko':
      const plinkoRisk = (searchParams.get('plinkoRisk') ??
        'medium') as PlinkoRisk
      const plinkoRows = Number(
        searchParams.get('plinkoRows') ?? 16
      ) as PlinkoRows

      const plinkoSlot = calculatePlinkoBetResult({
        clientSeed,
        serverSeed,
        nonce,
        risk: plinkoRisk,
        rows: plinkoRows,
      })

      const plinkoPayout = PLINKO_PAYOUTS[plinkoRisk][plinkoRows][plinkoSlot]
      const plinkoColor = PLINKO_PAYOUT_COLORS[plinkoRows][plinkoSlot]

      return (
        <Flex direction='column' w={12} h={10}>
          <PlinkoSlot
            slot={plinkoSlot}
            payout={plinkoPayout}
            color={plinkoColor}
          />
        </Flex>
      )

    case 'roulette':
      const rouletteResult = calculateRouletteBetResult({
        clientSeed,
        serverSeed,
        nonce,
      })

      return (
        <RouletteGameBoard result={rouletteResult} orientation='vertical' />
      )

    case 'tower':
      const towerDifficulty = (searchParams.get('towerDifficulty') ??
        'medium') as TowerGameDifficulty

      const towerResult = calculateTowerBetResult({
        clientSeed,
        serverSeed,
        nonce,
        difficulty: towerDifficulty,
      })

      return (
        <Flex
          position='relative'
          direction='column-reverse'
          gap={3}
          p={{ base: 4, lg: 8 }}
          w='full'
          maxW='500px'
        >
          {towerResult.map((row, rowIndex) => (
            <Flex
              key={rowIndex}
              position='relative'
              justify='center'
              gap={3}
              w='full'
            >
              {row.map((value, tileIndex) => (
                <TowerGameTile
                  key={tileIndex}
                  state='chosen'
                  status={value ? 'win' : 'lose'}
                />
              ))}
            </Flex>
          ))}
        </Flex>
      )

    case 'wheel':
      const wheelRisk = (searchParams.get('wheelRisk') ??
        'medium') as WheelGameRisk
      const wheelSegments = Number(
        searchParams.get('wheelSegments') ?? 30
      ) as WheelGameSegments

      const wheelPayoutIndex = calculateWheelBetResult({
        clientSeed,
        serverSeed,
        nonce,
        risk: wheelRisk,
        segments: wheelSegments,
      })

      return (
        <Box w='70%'>
          <WheelGameWheel
            payouts={WHEEL_PAYOUTS[wheelRisk][wheelSegments]}
            result={wheelPayoutIndex}
            disableAnimation
          />
        </Box>
      )
  }
}
